import { NextSeo } from 'next-seo';
import dynamic from 'next/dynamic';
import Head from 'next/head';
import { useRouter } from 'next/router';
import React from 'react';
import { HreflangTags } from '@/components/seo/HreflangTags';
import { useGlobalContext } from '@/context/Global';
import { useTranslationContext } from '@/context/translation';
import { useInitUserTrackingWeb } from '@/hooks/useInitUserTrackingWeb';
import type { SeoConfig } from '@/types';
import { ENV } from '@/constants/env';
import { DEFAULT_LOCALE, VALID_LOCALES } from '@/constants/locales';
import { PROJECT_TYPE } from '@/constants/projectType';
import seoImage from '@/public/seo-image.webp';
import styles from './SEOLandingLayout.module.scss';

// Props Types
interface IProps {
	children: React.ReactNode;
	config: SeoConfig;
	/** Extra class for the footer, e.g. to clear a page's fixed mobile CTA */
	footerClassName?: string;
}
const SEOHeader = dynamic(
	async () =>
		await import('@/components/modules/Landing/SEOHeader').then(
			mod => mod.SEOHeader,
		),
);
const SEOFooter = dynamic(
	async () =>
		await import('@/components/modules/Landing/SEOFooter').then(
			mod => mod.SEOFooter,
		),
);
export const SEOLandingLayout: React.FC<IProps> = ({
	children,
	config,
	footerClassName,
}) => {
	const { isLoading } = useGlobalContext();
	const { locale } = useTranslationContext();
	const router = useRouter();
	const doItOnce = React.useRef(true);

	// Sync path with address bar (same as LanguageSelector) so locale/hreflang stay correct after client-side language switch — router.asPath can lag or differ from actual URL with next-translate
	const [currentPath, setCurrentPath] = React.useState(
		() => router.asPath?.split('?')[0] || '/',
	);
	React.useEffect(() => {
		const updatePath = () =>
			setCurrentPath(
				typeof window !== 'undefined'
					? window.location.pathname
					: router.asPath?.split('?')[0] || '/',
			);
		updatePath();
		router.events.on('routeChangeComplete', updatePath);
		return () => router.events.off('routeChangeComplete', updatePath);
	}, [router.events, router.asPath]);

	const currentPathForHreflang = currentPath;

	const { title, description, ogTitle, ogDescription } = config || {};
	let normalizedTitle: string | undefined;
	if (title) {
		normalizedTitle = PROJECT_TYPE.PROD
			? title
			: `${title} [${ENV.PROJECT_TYPE}]`;
	}

	const currentLocale = locale || DEFAULT_LOCALE;
	const baseUrl = ENV.SEO_URL || 'https://liftapp.ai';

	useInitUserTrackingWeb(doItOnce);

	return (
		<div
			className={`${styles['layout']} ${
				isLoading ? styles['layout_opacity'] : ''
			}`}
		>
			<Head>
				<meta charSet='UTF-8' />
				<meta
					name='viewport'
					content='width=device-width, initial-scale=1.0, minimum-scale=1.0, maximum-scale=5.0'
				/>
				<meta httpEquiv='X-UA-Compatible' content='ie=edge' />
				<meta name='mobile-web-app-capable' content='yes' />
				<meta name='theme-color' content='#ffffff' />
			</Head>
			<HreflangTags
				currentPath={currentPathForHreflang}
				locales={VALID_LOCALES}
				defaultLocale={DEFAULT_LOCALE}
				baseUrl={baseUrl}
			/>
			<NextSeo
				title={normalizedTitle}
				description={description}
				canonical={config.canonical}
				openGraph={{
					url: config.canonical,
					title: ogTitle ?? normalizedTitle ?? undefined,
					description: ogDescription ?? description,
					locale: currentLocale,
					siteName: 'Lift',
					images: [
						{
							url: seoImage.src,
							width: 800,
							height: 600,
							alt: 'Lift App',
							type: 'image/webp',
						},
					],
				}}
			/>
			<SEOHeader />
			<main className='main'>{children}</main>
			<SEOFooter className={footerClassName} />
		</div>
	);
};
